import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";

interface CounterRow {
  id?: string;
  kind: string;
  value: number | string;
  previousValue?: number | string;
  difference?: number | string;
  previousReadDate?: ReactNode;
  currentReadDate?: ReactNode;
  previousProvider?: string;
  previousProviderCode?: string;
  currentProvider?: string;
  currentProviderCode?: string;
}

interface CounterDetailsTableProps {
  title?: string;
  data: CounterRow[];
  showEditButton?: boolean;
  onEdit?: (id: string) => void;
}

const getProviderColor = (providerCode: string): "default" | "primary" | "secondary" | "error" | "warning" | "info" | "success" => {
  if (!providerCode || providerCode === "-") return "default";

  const code = providerCode.toUpperCase();

  // User input - green
  if (code === "U") {
    return "success";
  }

  // Automatic/Import - blue
  if (code === "A" || code === "I") {
    return "info";
  }

  // Manual/Operator - orange
  if (code === "M" || code === "O") {
    return "warning";
  }

  // System - primary
  if (code === "S") {
    return "primary";
  }

  return "default";
};

// Cells wrap (breaking long values if needed) so the table always fits its
// container instead of scrolling horizontally in narrow modals.
export const wrappingTableSx = {
  width: "100%",
  "& .MuiTableCell-root": { px: 1, overflowWrap: "anywhere" },
};

const renderProviderChip = (provider?: string, providerCode?: string) =>
  provider && provider !== "-" ? (
    <Chip
      label={provider}
      size="small"
      color={getProviderColor(providerCode || "-")}
      sx={{
        height: 18,
        fontSize: "0.65rem",
        "& .MuiChip-label": {
          color: "inherit",
          px: 0.75,
        },
      }}
    />
  ) : (
    "-"
  );

export const CounterDetailsTable = ({
  title,
  data,
  showEditButton = false,
  onEdit,
}: CounterDetailsTableProps) => {
  const { t } = useTranslation();

  if (data.length === 0) {
    return null;
  }

  const renderSplitCell = (previous: ReactNode, current: ReactNode) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography variant="caption" component="div" sx={{ fontSize: "0.7rem", lineHeight: 1.4 }}>
        {t("Previous")}: {previous ?? "-"}
      </Typography>
      <Typography variant="caption" component="div" sx={{ fontSize: "0.7rem", lineHeight: 1.4 }}>
        {t("Current")}: {current ?? "-"}
      </Typography>
    </Box>
  );

  const renderStackedChips = (previous: ReactNode, current: ReactNode) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
      {previous}
      {current}
    </Box>
  );

  const hasEditableRow = showEditButton && data.some((row) => Boolean(row.id));

  return (
    <Stack spacing={0.5}>
      {title && <Typography variant="subtitle2">{title}</Typography>}
      <Table size="small" aria-label={title} sx={wrappingTableSx}>
        <TableHead>
          <TableRow>
            <TableCell>{t("Counter")}</TableCell>
            <TableCell>{t("Old counter")}</TableCell>
            <TableCell>{t("New counter")}</TableCell>
            <TableCell>{t("Counter diff")}</TableCell>
            <TableCell>{t("Read date")}</TableCell>
            <TableCell>{t("Counter source")}</TableCell>
            {hasEditableRow && <TableCell padding="checkbox" />}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow key={row.id || index}>
              <TableCell>{row.kind}</TableCell>
              <TableCell>{row.previousValue ?? "-"}</TableCell>
              <TableCell>{row.value ?? "-"}</TableCell>
              <TableCell>{row.difference ?? "-"}</TableCell>
              <TableCell>{renderSplitCell(row.previousReadDate, row.currentReadDate)}</TableCell>
              <TableCell>
                {renderStackedChips(
                  renderProviderChip(row.previousProvider, row.previousProviderCode),
                  renderProviderChip(row.currentProvider, row.currentProviderCode)
                )}
              </TableCell>
              {hasEditableRow && (
                <TableCell padding="checkbox">
                  {row.id ? (
                    <Tooltip title={t("Edit")}>
                      <IconButton size="small" onClick={() => onEdit?.(row.id!)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
};
